import React from 'react';
import { Box, Button, Paper, useTheme } from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { StatefulButton } from '../../NewAccountStepper/components/StatefulButton';
import { BaseStepperContextValue } from '../contexts/BaseStepperContext';
import { STEPPER_BUTTON_TEXTS } from '../constants';
import WarningMessage from './WarningMessage/WarningMessage';
import { stepperStyles } from '../styles';

export interface StepperFooterProps {
  stepperContext: BaseStepperContextValue & {
    handleSave?: () => void;
    error?: string | null;
  };
  showSkipButton?: boolean;
}

export const StepperFooter: React.FC<StepperFooterProps> = ({
  stepperContext,
  showSkipButton = false,
}) => {
  const theme = useTheme();
  const {
    activeStep,
    handleNext,
    handleBack,
    handleReset,
    isLastStep,
    warningMessage,
    showWarning,
    error,
    goToLastStep,
    numOfSteps,
  } = stepperContext;

  const isLast = isLastStep();
  const nextButtonText = isLast
    ? STEPPER_BUTTON_TEXTS.FINISH
    : STEPPER_BUTTON_TEXTS.NEXT;

  // Calculate the step limit dynamically
  const skipButtonStepLimit = numOfSteps() - 2; // Hide on last 2 steps

  return (
    <Paper elevation={0} sx={stepperStyles.footerContainer}>
      <Box sx={stepperStyles.buttonContainer}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Back Button */}
          {activeStep !== 0 && (
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={
                theme.direction === 'rtl' ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowLeft />
                )
              }
              sx={stepperStyles.secondaryButton}
            >
              {STEPPER_BUTTON_TEXTS.BACK}
            </Button>
          )}

          {/* Next/Finish Button */}
          {!isLast && (
            <StatefulButton
              variant="contained"
              onClick={handleNext}
              allowNavigation={true}
              endIcon={
                theme.direction === 'rtl' ? (
                  <KeyboardArrowLeft />
                ) : (
                  <KeyboardArrowRight />
                )
              }
              sx={stepperStyles.elevatedButton(theme)}
            >
              {nextButtonText}
            </StatefulButton>
          )}

          {/* Skip Button */}
          {showSkipButton && !isLast && activeStep < skipButtonStepLimit && (
            <Button
              variant="outlined"
              onClick={goToLastStep}
              sx={stepperStyles.secondaryButton}
            >
              {STEPPER_BUTTON_TEXTS.SKIP}
            </Button>
          )}

          {/* Save Button */}

          {/* Last Step Buttons */}
          {isLast && (
            <Button onClick={handleReset} sx={stepperStyles.secondaryButton}>
              {STEPPER_BUTTON_TEXTS.RESET_START_OVER}
            </Button>
          )}
        </Box>

        {/* Message Display */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {showWarning && warningMessage && (
            <WarningMessage
              message={warningMessage}
              show={showWarning}
              variant="warning"
            />
          )}

          {error && (
            <WarningMessage
              message={error}
              show={!!error}
              variant="error"
              icon={ErrorIcon}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default StepperFooter;
